import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import {
  Loader2, TrendingUp, Target, Eye, BarChart3, Layers,
  RefreshCw, Zap, Shield, Sparkles, ChevronDown, ChevronUp,
  ExternalLink, Clock, AlertTriangle, CheckCircle2, ArrowUpRight,
  MapPin, Crosshair, Rocket, Award, ArrowRight
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// Phase icons and colors
const PHASE_CONFIG = [
  { icon: Zap, color: '#ef4444', bg: 'bg-red-50', border: 'border-l-red-500', badge: 'bg-red-100 text-red-700', label: 'Quick Wins' },
  { icon: Layers, color: '#f59e0b', bg: 'bg-amber-50', border: 'border-l-amber-500', badge: 'bg-amber-100 text-amber-700', label: 'Foundation' },
  { icon: Rocket, color: '#10b981', bg: 'bg-emerald-50', border: 'border-l-emerald-500', badge: 'bg-emerald-100 text-emerald-700', label: 'Growth' },
];

const ENGINE_STATUS = {
  strong: { color: 'text-emerald-600', bg: 'bg-emerald-50', icon: CheckCircle2 },
  growing: { color: 'text-amber-600', bg: 'bg-amber-50', icon: TrendingUp },
  weak: { color: 'text-orange-600', bg: 'bg-orange-50', icon: AlertTriangle },
  absent: { color: 'text-red-600', bg: 'bg-red-50', icon: Eye },
};

const PRIORITY_BADGE = {
  high: 'bg-red-600 text-white',
  medium: 'bg-amber-500 text-white',
  low: 'bg-slate-400 text-white',
};

// Legacy type config for backward compatibility
const LEGACY_TYPE_CONFIG = {
  sov_boost: { icon: TrendingUp, color: 'border-l-emerald-500', bg: 'bg-emerald-50/40', badge: 'bg-emerald-100 text-emerald-700' },
  blind_spots: { icon: Eye, color: 'border-l-amber-500', bg: 'bg-amber-50/40', badge: 'bg-amber-100 text-amber-700' },
  competitor_counter: { icon: Shield, color: 'border-l-red-500', bg: 'bg-red-50/40', badge: 'bg-red-100 text-red-700' },
  content_plan: { icon: Layers, color: 'border-l-blue-500', bg: 'bg-blue-50/40', badge: 'bg-blue-100 text-blue-700' },
  platform_focus: { icon: Target, color: 'border-l-purple-500', bg: 'bg-purple-50/40', badge: 'bg-purple-100 text-purple-700' },
  content: { icon: Layers, color: 'border-l-blue-500', bg: 'bg-blue-50/40', badge: 'bg-blue-100 text-blue-700' },
  technical: { icon: Zap, color: 'border-l-orange-500', bg: 'bg-orange-50/40', badge: 'bg-orange-100 text-orange-700' },
  external: { icon: ExternalLink, color: 'border-l-purple-500', bg: 'bg-purple-50/40', badge: 'bg-purple-100 text-purple-700' },
  brand: { icon: Shield, color: 'border-l-emerald-500', bg: 'bg-emerald-50/40', badge: 'bg-emerald-100 text-emerald-700' },
};

const Strategy = ({ monitorId, monitor, geoId, geoLabel, geoLanguage }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [error, setError] = useState('');
  const [expandedPhase, setExpandedPhase] = useState(0);
  const [progressMsg, setProgressMsg] = useState('');
  const { t, i18n } = useTranslation();
  const dateLocale = i18n.language === 'ru' ? 'ru-RU' : 'en-US';
  const mountedRef = useRef(true);

  useEffect(() => { return () => { mountedRef.current = false; }; }, []);

  const loadCached = async () => {
    try {
      const params = geoId ? `?geo_id=${geoId}` : '';
      const { data: resp } = await axios.get(`${API}/api/promo/${monitorId}/insights${params}`, { withCredentials: true });
      setData(resp);
    } catch (err) {
      console.error(err);
    } finally {
      setInitialLoading(false);
    }
  };

  const pollTask = async (taskId) => {
    const maxWait = 600;
    let elapsed = 0;
    while (elapsed < maxWait) {
      try {
        const { data: td } = await axios.get(`${API}/api/tasks/${taskId}`, { withCredentials: true });
        setProgressMsg(td.progress || t('strategy.обработка'));
        if (td.status === 'completed') {
          await new Promise(r => setTimeout(r, 2000));
          await loadCached();
          return;
        }
        if (td.status === 'failed') throw new Error(td.error || t('strategy.ошибка_ai'));
      } catch (err) {
        if (err.message?.includes(t('strategy.ошибка_ai'))) throw err;
      }
      await new Promise(r => setTimeout(r, 3000));
      elapsed += 3;
    }
    throw new Error(t('strategy.таймаут'));
  };

  const handleRefresh = async () => {
    setLoading(true);
    setError('');
    setProgressMsg(t('strategy.запуск'));
    try {
      const body = geoId ? { geo_id: geoId, locale: geoLanguage || 'en' } : {};
      const { data: resp } = await axios.post(`${API}/api/promo/${monitorId}/refresh-strategy`, body, { withCredentials: true });
      if (resp.task_id) {
        await pollTask(resp.task_id);
      } else {
        setData(resp);
      }
      setExpandedPhase(0);
      setLoading(false);
      setProgressMsg('');
    } catch (err) {
      if (err.response?.status === 409) {
        setProgressMsg(t('strategy.генерация_продолжается'));
        pollOperationDone();
        return;
      }
      setError(err.response?.data?.detail || err.message || t('strategy.ошибка_генерации'));
      setLoading(false);
      setProgressMsg('');
    }
  };

  useEffect(() => {
    setInitialLoading(true);
    setData(null);
    setError('');
    setExpandedPhase(0);
    loadCached();
  }, [monitorId, geoId, geoLanguage]); // eslint-disable-line react-hooks/exhaustive-deps

  // Resume operation state on page return
  useEffect(() => {
    const checkActive = async () => {
      try {
        const { data: opData } = await axios.get(`${API}/api/promo/${monitorId}/operation-status`, { withCredentials: true });
        if (!mountedRef.current) return;
        if (opData.active_operation === 'refresh_strategy') {
          setLoading(true);
          setProgressMsg(`${t('strategy.генерация_продолжается')} ${Math.floor((opData.elapsed_seconds || 0) / 60)}м`);
          pollOperationDone();
        }
      } catch (err) { /* ignore */ }
    };
    checkActive();
  }, [monitorId]); // eslint-disable-line react-hooks/exhaustive-deps

  const pollOperationDone = async () => {
    const maxWait = 600;
    let elapsed = 0;
    while (elapsed < maxWait) {
      await new Promise(r => setTimeout(r, 3000));
      elapsed += 3;
      if (!mountedRef.current) return;
      try {
        const { data: opData } = await axios.get(`${API}/api/promo/${monitorId}/operation-status`, { withCredentials: true });
        if (!mountedRef.current) return;
        if (!opData.active_operation) {
          await loadCached();
          setLoading(false);
          setProgressMsg('');
          return;
        }
        setProgressMsg(`${t('strategy.генерация_загрузка')} ${Math.floor((opData.elapsed_seconds || 0) / 60)}м ${(opData.elapsed_seconds || 0) % 60}с`);
      } catch (err) { /* continue */ }
    }
    setLoading(false);
    setProgressMsg('');
    setError(t('strategy.таймаут_генерации'));
  };

  const formatDate = (isoStr) => {
    if (!isoStr) return '';
    try {
      return new Date(isoStr).toLocaleDateString(dateLocale, { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' });
    } catch { return ''; }
  };

  // Detect v2 format
  const isV2 = data && (data.version === 'v2' || (data.phases && data.phases.length > 0));
  const phases = isV2 ? (data.phases || []) : [];
  const execSummary = isV2 ? (data.executive_summary || {}) : {};
  const enginePlaybook = isV2 ? (data.engine_playbook || []) : [];
  const strategies = data ? (data.strategies || []) : [];
  const marketSummary = data ? (data.market_summary || '') : '';
  const sources = data ? (data.perplexity_sources || []) : [];
  const generatedAt = data ? (data.generated_at || null) : null;
  const hasData = isV2 ? phases.length > 0 : strategies.length > 0;

  if (initialLoading) {
    return (
      <div className="py-16 text-center">
        <Loader2 className="w-6 h-6 text-[#1BB594] animate-spin mx-auto mb-3" />
        <p className="text-sm text-slate-400">{t('strategy.загрузка')}</p>
      </div>
    );
  }

  // ────────────────────────────────────────────
  // LEGACY RENDER (old hypothesis format)
  // ────────────────────────────────────────────
  const renderLegacy = () => (
    <div className="space-y-3">
      {strategies.map((s, i) => {
        const cfg = LEGACY_TYPE_CONFIG[s.type] || LEGACY_TYPE_CONFIG[s.category] || LEGACY_TYPE_CONFIG.sov_boost;
        const Icon = cfg.icon;
        const isExpanded = expandedPhase === i;
        const actionItems = s.action_items || s.actions || (s.tasks ? s.tasks.map(task => typeof task === 'string' ? task : task.text) : []);
        const expectedImpact = s.expected_impact || s.expected_result || (s.verification ? s.verification.expected_signal : '') || '';
        const engines = s.engines || s.platforms || [];
        return (
          <div key={i} className={`border border-[#E2DDD8] border-l-4 rounded overflow-hidden transition-all ${cfg.color} ${isExpanded ? cfg.bg : 'bg-white hover:bg-slate-50/50'}`}>
            <button onClick={() => setExpandedPhase(isExpanded ? null : i)} className="w-full text-left p-4 flex items-start gap-3">
              <Icon className="w-5 h-5 text-slate-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  <span className={`text-[10px] px-2 py-0.5 rounded font-bold ${cfg.badge}`}>{s.type || s.category || 'strategy'}</span>
                  {s.priority && <span className={`text-[10px] px-2 py-0.5 rounded font-bold ${PRIORITY_BADGE[s.priority?.toLowerCase()] || 'bg-slate-200 text-slate-600'}`}>{s.priority}</span>}
                  {engines.length > 0 && <span className="text-[10px] text-slate-400">{engines.join(', ')}</span>}
                </div>
                <h4 className="text-sm font-bold text-slate-800">{s.title}</h4>
                {!isExpanded && <p className="text-xs text-slate-500 mt-1 line-clamp-2">{s.description || s.hypothesis || ''}</p>}
              </div>
              {isExpanded ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
            </button>
            {isExpanded && (
              <div className="px-4 pb-4 pt-0 ml-8">
                <p className="text-sm text-slate-600 leading-relaxed mb-4">{s.description || s.hypothesis || [s.fact, s.gap].filter(Boolean).join(' ')}</p>
                {actionItems.length > 0 && (
                  <div className="mb-4">
                    <h5 className="text-xs font-bold uppercase tracking-wider text-slate-700 mb-2">{t('strategy.план_действий')}</h5>
                    <div className="space-y-1.5">
                      {actionItems.map((action, ai) => (
                        <div key={ai} className="flex items-start gap-2">
                          <CheckCircle2 className="w-3.5 h-3.5 text-[#1BB594] flex-shrink-0 mt-0.5" />
                          <p className="text-xs text-slate-600">{action}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {expectedImpact && (
                  <div className="flex items-start gap-2 p-3 bg-[#1BB594]/5 border border-[#1BB594]/15 rounded">
                    <ArrowUpRight className="w-4 h-4 text-[#1BB594] flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-[10px] font-bold uppercase tracking-wider text-[#1BB594] mb-0.5">{t('strategy.ожидаемый_результат')}</p>
                      <p className="text-xs text-slate-700">{expectedImpact}</p>
                    </div>
                  </div>
                )}
                {s.fact && <div className="mt-3 text-xs text-slate-500"><span className="font-bold">{t('strategy.факт')}:</span> {s.fact}</div>}
                {s.gap && <div className="mt-1 text-xs text-slate-500"><span className="font-bold">{t('strategy.разрыв')}:</span> {s.gap}</div>}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );

  // ────────────────────────────────────────────
  // V2 RENDER (phased roadmap)
  // ────────────────────────────────────────────
  const renderV2 = () => (
    <div className="space-y-6">
      {/* Executive Summary */}
      {(execSummary.current_position || execSummary.competitive_landscape || execSummary.key_opportunity) && (
        <div className="p-5 bg-gradient-to-br from-slate-50 to-emerald-50/30 border border-[#E2DDD8] rounded-lg">
          <div className="flex items-center gap-2 mb-3">
            <Crosshair className="w-4 h-4 text-[#1BB594]" />
            <h4 className="text-xs font-bold uppercase tracking-widest text-slate-700">Executive Summary</h4>
          </div>
          <div className="space-y-2">
            {execSummary.current_position && (
              <p className="text-sm text-slate-700"><span className="font-semibold text-slate-800">Position:</span> {execSummary.current_position}</p>
            )}
            {execSummary.competitive_landscape && (
              <p className="text-sm text-slate-700"><span className="font-semibold text-slate-800">Competition:</span> {execSummary.competitive_landscape}</p>
            )}
            {execSummary.key_opportunity && (
              <p className="text-sm text-[#1BB594] font-medium"><span className="font-semibold">Opportunity:</span> {execSummary.key_opportunity}</p>
            )}
          </div>
        </div>
      )}

      {/* Phases */}
      {phases.map((phase, phaseIdx) => {
        const cfg = PHASE_CONFIG[phaseIdx] || PHASE_CONFIG[2];
        const PhaseIcon = cfg.icon;
        const isExpanded = expandedPhase === phaseIdx;
        const actions = phase.actions || [];

        return (
          <div key={phaseIdx} className={`border border-[#E2DDD8] border-l-4 rounded-lg overflow-hidden ${cfg.border}`}>
            {/* Phase header */}
            <button
              onClick={() => setExpandedPhase(isExpanded ? null : phaseIdx)}
              className={`w-full text-left p-4 flex items-center gap-3 transition-colors ${isExpanded ? cfg.bg : 'bg-white hover:bg-slate-50/50'}`}
            >
              <div className="flex items-center justify-center w-8 h-8 rounded-full" style={{ backgroundColor: cfg.color + '15' }}>
                <PhaseIcon className="w-4 h-4" style={{ color: cfg.color }} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-0.5">
                  <span className={`text-[10px] px-2 py-0.5 rounded font-bold ${cfg.badge}`}>
                    {phase.timeline || `Phase ${phaseIdx + 1}`}
                  </span>
                  <span className="text-[10px] text-slate-400">{actions.length} {actions.length === 1 ? 'action' : 'actions'}</span>
                </div>
                <h4 className="text-sm font-bold text-slate-800">{phase.name || cfg.label}</h4>
                {!isExpanded && phase.description && (
                  <p className="text-xs text-slate-500 mt-0.5 line-clamp-1">{phase.description}</p>
                )}
              </div>
              {isExpanded ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
            </button>

            {/* Phase content */}
            {isExpanded && (
              <div className="px-4 pb-4 space-y-3">
                {phase.description && (
                  <p className="text-sm text-slate-600 ml-11">{phase.description}</p>
                )}
                {actions.map((action, actionIdx) => {
                  const engines = action.engines || [];
                  const tasks = action.tasks || [];
                  return (
                    <div key={actionIdx} className="ml-11 p-3 bg-white border border-slate-200 rounded-lg">
                      {/* Action header */}
                      <div className="flex items-start justify-between gap-2 mb-2">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 flex-wrap mb-1">
                            {action.priority && (
                              <span className={`text-[9px] px-1.5 py-0.5 rounded font-bold ${PRIORITY_BADGE[action.priority?.toLowerCase()] || 'bg-slate-200 text-slate-600'}`}>
                                {action.priority.toUpperCase()}
                              </span>
                            )}
                            {engines.map((eng, ei) => (
                              <span key={ei} className="text-[9px] px-1.5 py-0.5 rounded bg-blue-50 text-blue-600 font-medium">{eng}</span>
                            ))}
                          </div>
                          <h5 className="text-sm font-semibold text-slate-800">{action.title}</h5>
                        </div>
                      </div>

                      {/* Rationale */}
                      {action.rationale && (
                        <div className="mb-2 p-2 bg-slate-50 rounded text-xs text-slate-600">
                          <span className="font-semibold text-slate-700">Why: </span>{action.rationale}
                        </div>
                      )}

                      {/* Tasks */}
                      {tasks.length > 0 && (
                        <div className="space-y-1 mb-2">
                          {tasks.map((task, ti) => (
                            <div key={ti} className="flex items-start gap-2">
                              <ArrowRight className="w-3 h-3 text-[#1BB594] flex-shrink-0 mt-0.5" />
                              <p className="text-xs text-slate-600">{typeof task === 'string' ? task : task.text}</p>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Expected signal */}
                      {action.expected_signal && (
                        <div className="flex items-start gap-1.5 pt-2 border-t border-slate-100">
                          <Target className="w-3 h-3 text-[#1BB594] flex-shrink-0 mt-0.5" />
                          <p className="text-[11px] text-slate-500">{action.expected_signal}</p>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}

      {/* Engine Playbook */}
      {enginePlaybook.length > 0 && (
        <div className="border border-[#E2DDD8] rounded-lg p-4">
          <div className="flex items-center gap-2 mb-3">
            <Award className="w-4 h-4 text-slate-600" />
            <h4 className="text-xs font-bold uppercase tracking-widest text-slate-700">Engine Playbook</h4>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {enginePlaybook.map((ep, i) => {
              const statusCfg = ENGINE_STATUS[ep.status] || ENGINE_STATUS.weak;
              const StatusIcon = statusCfg.icon;
              return (
                <div key={i} className={`p-3 rounded-lg border border-slate-200 ${statusCfg.bg}`}>
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-sm font-bold text-slate-800">{ep.engine}</span>
                    <div className="flex items-center gap-1.5">
                      <StatusIcon className={`w-3.5 h-3.5 ${statusCfg.color}`} />
                      <span className={`text-[10px] font-bold ${statusCfg.color}`}>{ep.current_sov || ep.status}</span>
                    </div>
                  </div>
                  {ep.key_action && <p className="text-xs text-slate-600 mb-1">{ep.key_action}</p>}
                  {ep.platforms && ep.platforms.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-1.5">
                      {ep.platforms.map((p, pi) => (
                        <span key={pi} className="text-[9px] px-1.5 py-0.5 bg-white border border-slate-200 rounded text-slate-500">{p}</span>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
        <div>
          <h3 className="text-sm font-bold uppercase tracking-widest text-slate-800">
            {t('strategy.стратегии_продвижения')}
          </h3>
          <p className="text-xs text-slate-400 mt-1">
            {geoLabel ? `AI strategy for ${geoLabel}` : t('strategy.ai_анализ_рынка')}
          </p>
        </div>
        <div className="flex items-center gap-3">
          {geoLabel && (
            <span className="flex items-center gap-1.5 px-2.5 py-1 bg-emerald-50 border border-[#1BB594]/30 text-[#1BB594] text-[10px] font-bold uppercase tracking-wider">
              <MapPin className="w-3 h-3" />
              {geoLabel}
            </span>
          )}
          {generatedAt && (
            <span className="flex items-center gap-1 text-[10px] text-slate-400">
              <Clock className="w-3 h-3" />
              {formatDate(generatedAt)}
            </span>
          )}
          <button
            onClick={handleRefresh}
            disabled={loading}
            className="flex items-center gap-1.5 px-4 py-2 bg-[#1BB594] text-white text-xs font-bold uppercase tracking-widest hover:bg-[#159A7F] disabled:opacity-50 transition-colors"
          >
            {loading ? (
              <><Loader2 className="w-3.5 h-3.5 animate-spin" /> {t('strategy.генерация_загрузка')}</>
            ) : (
              <><RefreshCw className="w-3.5 h-3.5" /> {geoLabel ? `Generate for ${geoLabel}` : t('strategy.обновить_стратегии')}</>
            )}
          </button>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
          <p className="text-xs text-red-700">{error}</p>
        </div>
      )}

      {/* Loading overlay */}
      {loading && (
        <div className="mb-6 p-6 bg-gradient-to-r from-[#1BB594]/5 to-transparent border border-[#1BB594]/20 rounded">
          <div className="flex items-center gap-3 mb-3">
            <Loader2 className="w-5 h-5 text-[#1BB594] animate-spin" />
            <div>
              <p className="text-sm font-bold text-slate-800">
                {geoLabel ? `Generating strategy for ${geoLabel}...` : t('strategy.генерация_стратегий')}
              </p>
              <p className="text-xs text-slate-400">{progressMsg || t('strategy.подготовка')}</p>
            </div>
          </div>
          <div className="w-full bg-slate-100 rounded-full h-1.5 overflow-hidden">
            <div className="bg-[#1BB594] h-full rounded-full" style={{ width: '100%', animation: 'progress-indeterminate 2s ease-in-out infinite' }} />
          </div>
        </div>
      )}

      {/* Market Summary */}
      {marketSummary && !loading && (
        <div className="mb-5 p-4 bg-slate-50 border border-[#E2DDD8] rounded">
          <div className="flex items-center gap-2 mb-2">
            <BarChart3 className="w-4 h-4 text-slate-600" />
            <h4 className="text-xs font-bold uppercase tracking-wider text-slate-700">{t('strategy.обзор_рынка')}</h4>
          </div>
          <p className="text-sm text-slate-600 leading-relaxed">{marketSummary}</p>
        </div>
      )}

      {/* Main content */}
      {!loading && !hasData ? (
        <div className="py-16 text-center border border-[#E2DDD8] rounded">
          <TrendingUp className="w-10 h-10 mx-auto mb-3 text-slate-200" />
          {geoLabel ? (
            <>
              <p className="text-sm text-slate-400 mb-1">No strategy generated for {geoLabel} yet</p>
              <p className="text-xs text-slate-300 mb-4">Click the button above to generate a location-specific strategy using real monitoring data</p>
            </>
          ) : (
            <>
              <p className="text-sm text-slate-400 mb-1">{t('strategy.нет_стратегий')}</p>
              <p className="text-xs text-slate-300">{t('strategy.нет_стратегий_подсказка')}</p>
            </>
          )}
        </div>
      ) : !loading && (
        isV2 ? renderV2() : renderLegacy()
      )}

      {/* Data Sources */}
      {!loading && sources.length > 0 && (
        <div className="mt-5 p-4 bg-slate-50/50 border border-[#E2DDD8] rounded">
          <h4 className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-2">{t('strategy.источники_данных')}</h4>
          <div className="flex flex-wrap gap-2">
            {sources.map((src, i) => (
              <a key={i} href={src} target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-[10px] text-slate-500 hover:text-[#1BB594] transition-colors bg-white border border-[#E2DDD8] rounded px-2 py-1">
                <ExternalLink className="w-2.5 h-2.5" />
                {(() => { try { return new URL(src).hostname.replace('www.', ''); } catch { return src.slice(0, 30); } })()}
              </a>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default Strategy;
